import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type { Game, ActiveQuestion, QuestionResults, FinalResults, Team, SabotageAction } from '../types';
import { SOCKET_URL } from '../config';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  game: Game | null;
  activeQuestion: ActiveQuestion | null;
  timeRemaining: number;
  questionResults: QuestionResults | null;
  finalResults: FinalResults | null;
  error: string | null;
  
  // Actions
  joinGame: (gameId: string, teamName: string, players: string[]) => void;
  joinAsAdmin: (gameId: string) => void;
  startGame: (gameId: string) => void;
  nextQuestion: (gameId: string) => void;
  skipQuestion: (gameId: string) => void;
  applySabotage: (gameId: string, sabotage: SabotageAction) => void;
  submitAnswer: (gameId: string, teamId: string, questionId: string, answer: string) => void;
  voteMol: (gameId: string, teamId: string, molVote: string) => void;
  revealMol: (gameId: string) => void;
  clearError: () => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [game, setGame] = useState<Game | null>(null);
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestion | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [questionResults, setQuestionResults] = useState<QuestionResults | null>(null);
  const [finalResults, setFinalResults] = useState<FinalResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setIsConnected(false);
    });

    newSocket.on('game:state', (gameData: Game) => {
      setGame(gameData);
    });

    newSocket.on('game:started', () => {
      console.log('Game started!');
    });

    newSocket.on('question:show', (data: ActiveQuestion) => {
      setActiveQuestion(data);
      setTimeRemaining(data.timeRemaining);
      setQuestionResults(null);
    });

    newSocket.on('question:timer', (remaining: number) => {
      setTimeRemaining(remaining);
    });

    newSocket.on('question:ended', (results: QuestionResults) => {
      setQuestionResults(results);
      setActiveQuestion(null);
    });

    newSocket.on('sabotage:applied', (sabotage: SabotageAction) => {
      console.log('Sabotage applied:', sabotage);
    });

    newSocket.on('voting:started', () => {
      console.log('Voting started!');
      setActiveQuestion(null);
    });

    newSocket.on('game:finished', (results: FinalResults) => {
      setFinalResults(results);
    });

    newSocket.on('team:joined', (team: Team) => {
      console.log('Team joined:', team.name);
    });

    newSocket.on('team:left', (teamId: string) => {
      console.log('Team left:', teamId);
    });

    newSocket.on('error', (message: string) => {
      setError(message);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const joinGame = (gameId: string, teamName: string, players: string[]) => {
    socket?.emit('game:join', { gameId, teamName, players });
  };

  const joinAsAdmin = (gameId: string) => {
    socket?.emit('admin:join', { gameId });
  };

  const startGame = (gameId: string) => {
    socket?.emit('game:start', gameId);
  };

  const nextQuestion = (gameId: string) => {
    socket?.emit('question:next', gameId);
  };

  const skipQuestion = (gameId: string) => {
    socket?.emit('question:skip', gameId);
  };

  const applySabotage = (gameId: string, sabotage: SabotageAction) => {
    socket?.emit('sabotage:apply', { gameId, sabotage });
  };

  const submitAnswer = (gameId: string, teamId: string, questionId: string, answer: string) => {
    socket?.emit('answer:submit', { gameId, teamId, questionId, answer });
  };

  const voteMol = (gameId: string, teamId: string, molVote: string) => {
    socket?.emit('vote:mol', { gameId, teamId, molVote });
  };

  const revealMol = (gameId: string) => {
    socket?.emit('game:reveal', gameId);
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        game,
        activeQuestion,
        timeRemaining,
        questionResults,
        finalResults,
        error,
        joinGame,
        joinAsAdmin,
        startGame,
        nextQuestion,
        skipQuestion,
        applySabotage,
        submitAnswer,
        voteMol,
        revealMol,
        clearError,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}
